import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, PencilIcon, TrashIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import userService from '../../services/userService';
import roleService from '../../services/roleService';
import { toast } from 'react-hot-toast';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    roleId: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [u, r] = await Promise.all([
        userService.list(),
        roleService.listRoles(),
      ]);
      setUsers(u.data?.users || u.data || []);
      setRoles(r.data?.roles || r.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load users/roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await userService.update(editing.id, form);
        toast.success('User updated');
      } else {
        await userService.create(form);
        toast.success('User created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ firstName: '', lastName: '', email: '', password: '', roleId: '' });
      loadData();
    } catch (e) {
      console.error(e);
      toast.error('Save failed');
    }
  };

  const remove = async (user) => {
    if (!confirm('Delete this user?')) return;
    try {
      await userService.remove(user.id);
      toast.success('User deleted');
      loadData();
    } catch (e) {
      console.error(e);
      toast.error('Delete failed');
    }
  };

  const assignRole = async (user, roleId) => {
    try {
      await userService.assignRole(user.id, roleId);
      toast.success('Role updated');
      loadData();
    } catch (e) {
      console.error(e);
      toast.error('Role update failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
            <p className="text-gray-600">Create, edit and assign roles to users</p>
          </div>
          <button onClick={() => { setShowForm(true); setEditing(null); }} className="px-3 py-2 bg-blue-600 text-white rounded-md flex items-center">
            <UserPlusIcon className="w-5 h-5 mr-2" /> New User
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-6 py-4">{u.firstName} {u.lastName}</td>
                    <td className="px-6 py-4">{u.email}</td>
                    <td className="px-6 py-4">
                      <select
                        className="border rounded px-2 py-1"
                        value={u.Role?.id || ''}
                        onChange={(e) => assignRole(u, e.target.value)}
                      >
                        <option value="">Select role</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        className="text-indigo-600 hover:text-indigo-800"
                        onClick={() => { setEditing(u); setShowForm(true); setForm({ firstName: u.firstName, lastName: u.lastName, email: u.email, password: '', roleId: u.Role?.id || '' }); }}
                      >
                        <PencilIcon className="w-4 h-4 inline" />
                      </button>
                      <button className="text-red-600 hover:text-red-800" onClick={() => remove(u)}>
                        <TrashIcon className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 w-full max-w-lg">
              <h2 className="text-lg font-semibold mb-4">{editing ? 'Edit User' : 'New User'}</h2>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input className="border rounded px-3 py-2" placeholder="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                  <input className="border rounded px-3 py-2" placeholder="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                </div>
                <input className="border rounded px-3 py-2 w-full" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                {!editing && (
                  <input className="border rounded px-3 py-2 w-full" placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                )}
                <select className="border rounded px-3 py-2 w-full" value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
                  <option value="">Select role</option>
                  {roles.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
                </select>
                <div className="flex justify-end space-x-2 pt-2">
                  <button type="button" className="px-4 py-2 border rounded" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersManagement;



